import { ArrowLeft, ExternalLink } from "lucide-react";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";

interface PageContent {
  kicker: string;
  title: string;
  intro: string;
  sections: Array<{ title: string; body: string[] }>;
  links?: Array<{ label: string; href: string }>;
}

const pages: Record<string, PageContent> = {
  metodologia: {
    kicker: "Metodologia",
    title: "A matemática precisa caber na conversa.",
    intro:
      "O Alicerce compara estruturas com premissas explícitas. Ele ajuda a formular perguntas melhores; não substitui proposta bancária, análise jurídica ou aconselhamento financeiro.",
    sections: [
      {
        title: "SAC e Price",
        body: [
          "No SAC, a amortização do principal é constante. Como o saldo cai mais rápido, juros e prestação tendem a diminuir ao longo do tempo.",
          "Na Price, a prestação-base é nivelada pela fórmula de anuidade. Seguro e tarifa mensal podem fazer o valor total variar mesmo quando a parcela financeira é constante."
        ]
      },
      {
        title: "Taxas e custos",
        body: [
          "A taxa anual efetiva é convertida para a equivalente mensal por capitalização composta. Seguro ilustrativo incide sobre o saldo de abertura; tarifa administrativa é somada em cada período.",
          "O CET estimado é obtido pela taxa interna de retorno entre o valor líquido financiado e todos os pagamentos simulados. O CET oficial só existe na proposta da instituição."
        ]
      },
      {
        title: "Enquadramento",
        body: [
          "A leitura preliminar cruza quota financiada, prazo, idade ao final do contrato e comprometimento da renda pela primeira parcela. Instituições podem aplicar critérios, seguros e políticas adicionais.",
          "As instituições e condições desta demonstração são fictícias e ilustrativas. O painel permite substituí-las por parceiros e premissas vigentes."
        ]
      }
    ],
    links: [
      {
        label: "Banco Central — Custo Efetivo Total",
        href: "https://www.bcb.gov.br/estabilidadefinanceira/exibenormativo?tipo=Resolu%C3%A7%C3%A3o%20CMN&numero=4881"
      },
      {
        label: "CAIXA — sistemas de amortização",
        href: "https://www.caixa.gov.br/voce/habitacao/Paginas/default.aspx"
      }
    ]
  },
  privacidade: {
    kicker: "Privacidade",
    title: "Simular não exige identificação.",
    intro:
      "Os cálculos são feitos no navegador. A leitura opcional com IA envia somente um resumo financeiro sem identificadores; informações de contato só seguem ao servidor com consentimento.",
    sections: [
      {
        title: "Dados tratados",
        body: [
          "Quando você pede contato, registramos nome, e-mail, telefone, data, premissas da simulação e opção selecionada. Cenários salvos permanecem apenas no armazenamento local do navegador.",
          "Não realizamos consulta ao CPF, bureaus de crédito ou instituições financeiras por meio desta demonstração."
        ]
      },
      {
        title: "Leitura com Gemini",
        body: [
          "A leitura inteligente só é acionada por você. Ela envia ao servidor valor do imóvel, entrada, prazo e resultados resumidos da opção escolhida e das alternativas. Nome, e-mail, telefone, CPF, idade, renda bruta e cronograma completo não fazem parte desse payload. A proximidade da parcela ao limite configurado é enviada apenas como uma faixa qualitativa, sem percentual exato.",
          "O servidor encaminha esse resumo ao Google Gemini com armazenamento da interação desativado na chamada. A resposta é educativa, pode conter imprecisões e não interfere no cálculo SAC ou Price."
        ]
      },
      {
        title: "Finalidade e retenção",
        body: [
          "Os dados de lead servem exclusivamente para retomar o estudo solicitado. Na implantação comercial, o controlador deve definir prazo de retenção, canal de exercício de direitos e operadores envolvidos.",
          "Você pode solicitar confirmação, correção ou eliminação pelo canal informado na página de suporte da operação responsável."
        ]
      },
      {
        title: "Segurança",
        body: [
          "O painel usa sessão HTTP-only, limite de tentativas e credenciais mantidas no ambiente de execução. A aplicação não registra a credencial administrativa em banco ou navegador.",
          "Nenhum sistema é infalível; incidentes devem seguir o plano de resposta da operação que adotar esta solução."
        ]
      }
    ]
  },
  termos: {
    kicker: "Termos de uso",
    title: "Uma comparação informativa, não uma promessa.",
    intro:
      "Ao usar o Alicerce, você reconhece que os resultados são estimativas baseadas nos valores e parâmetros disponíveis no momento.",
    sections: [
      {
        title: "Limites",
        body: [
          "O resultado não representa aprovação, proposta, compromisso de taxa, aconselhamento jurídico ou recomendação individual de contratação.",
          "Confirme taxa, CET, seguros, tarifas, indexadores, condições de elegibilidade e documentos diretamente na proposta oficial. Conteúdo gerado por IA também deve ser validado e pode apresentar imprecisões."
        ]
      },
      {
        title: "Responsabilidade do uso",
        body: [
          "Você é responsável pela exatidão dos dados informados e pela decisão final. Resultados podem mudar com arredondamento, data, perfil, política de crédito e custos não configurados.",
          "As marcas fictícias da demonstração não representam instituições financeiras reais."
        ]
      }
    ]
  },
  acessibilidade: {
    kicker: "Acessibilidade",
    title: "Decisão clara também é decisão acessível.",
    intro:
      "A experiência foi construída para navegação por teclado, leitores de tela, zoom, redução de movimento e contraste consistente.",
    sections: [
      {
        title: "Recursos incluídos",
        body: [
          "Estrutura semântica, link de salto, rótulos explícitos, estados de foco visíveis, mensagens ao vivo, tabelas com cabeçalhos e gráficos com descrições textuais.",
          "O layout se reorganiza para telas estreitas sem esconder funções centrais. Animações respeitam a preferência de movimento reduzido."
        ]
      },
      {
        title: "Reportar uma barreira",
        body: [
          "Se algo impedir o uso, registre dispositivo, navegador, tecnologia assistiva e o ponto da jornada. A operação responsável deve publicar um contato acessível antes do lançamento comercial."
        ]
      }
    ]
  },
  sobre: {
    kicker: "Sobre",
    title: "Alicerce é uma ferramenta de decisão.",
    intro:
      "A proposta é transformar uma comparação normalmente opaca em uma superfície auditável, útil para consumidores, correspondentes e times comerciais.",
    sections: [
      {
        title: "O que resolve",
        body: [
          "Calcula simultaneamente SAC e Price para múltiplas instituições, expõe elegibilidade preliminar, permite explorar cronogramas e preserva o cenário de um lead.",
          "O painel desacopla condições comerciais do código: bancos, taxas, limites e custos podem ser ajustados pela operação."
        ]
      },
      {
        title: "Próxima camada",
        body: [
          "A leitura opcional com Gemini já transforma o cenário em pontos de atenção e perguntas de negociação sem tocar no motor financeiro. A arquitetura também suporta CRM, autenticação por equipe, trilha de auditoria, tabelas de seguros por idade, indexadores e propostas reais via API."
        ]
      }
    ]
  },
  suporte: {
    kicker: "Suporte",
    title: "Chegue com o cenário, não com a dúvida solta.",
    intro:
      "Na implantação comercial, esta página deve receber o canal oficial do operador. Para esta demonstração, use o formulário de contato da própria simulação.",
    sections: [
      {
        title: "Para analisar uma simulação",
        body: [
          "Abra a opção desejada, revise o cronograma e clique em “Receber estudo”. As premissas e a estrutura selecionada seguem juntas para evitar retrabalho.",
          "Nunca envie documentos pessoais, senhas ou dados bancários pelo campo de contato."
        ]
      },
      {
        title: "Para reportar falhas",
        body: [
          "Informe navegador, horário, passos para reproduzir e resultado esperado. Não inclua credenciais, cookies ou informações financeiras sensíveis."
        ]
      }
    ]
  },
  seguranca: {
    kicker: "Segurança",
    title: "Controles proporcionais ao que a aplicação guarda.",
    intro:
      "O Alicerce minimiza dados, separa acesso administrativo e mantém segredos fora do código e do cliente.",
    sections: [
      {
        title: "Controles técnicos",
        body: [
          "Cabeçalhos de segurança, política de conteúdo, limites separados para leads, login e IA, validação estrita de payload, cookie HTTP-only e SameSite, credenciais de ambiente e consultas parametrizadas.",
          "O banco guarda apenas configurações e leads consentidos. A aplicação não armazena CPF, renda documental, senha de usuário ou dados de pagamento."
        ]
      },
      {
        title: "Operação",
        body: [
          "Produção exige rotação segura de credenciais, backups testados, monitoramento, controle de acesso ao Railway e revisão periódica de dependências.",
          "Vulnerabilidades devem ser reportadas de forma privada ao responsável pela instância, sem exposição pública de detalhes exploráveis."
        ]
      }
    ]
  }
};

export function ContentPage({ slug }: { slug: string }) {
  const page = pages[slug] ?? pages.sobre;
  return (
    <>
      <Header />
      <main className="content-page">
        <a className="back-link" href="/">
          <ArrowLeft size={16} aria-hidden="true" /> Voltar ao simulador
        </a>
        <article>
          <header>
            <span className="section-kicker">{page.kicker}</span>
            <h1>{page.title}</h1>
            <p>{page.intro}</p>
          </header>
          {page.sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}
          {page.links && (
            <aside className="source-links">
              <strong>Fontes para aprofundar</strong>
              {page.links.map((link) => (
                <a href={link.href} key={link.href} target="_blank" rel="noreferrer">
                  {link.label} <ExternalLink size={14} aria-hidden="true" />
                </a>
              ))}
            </aside>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}
